import React, { useState, useEffect, useMemo, useCallback, useRef } from "react";
import {
  Search,
  Calendar,
  Clock,
  MoreVertical,
  Eye,
  Trash2,
  RefreshCw,
  Grid,
  List,
  TrendingUp,
  Video,
  FileText,
  ChevronLeft,
  ChevronRight,
  Folder,
  FolderOpen,
  Check,
} from "lucide-react";
import { fetchMeetings, deleteMeeting, apiClient } from "../services/api";
import { useAuth } from "../contexts/AuthContext";
import MeetingDetails from "./MeetingDetails";

const Meetings = () => {
  const { user, isAuthenticated } = useAuth();
  const [meetings, setMeetings] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState("");
  const [debouncedSearch, setDebouncedSearch] = useState("");
  const searchDebounceRef = useRef(null);
  const [showMeetingDetails, setShowMeetingDetails] = useState(false);
  const [selectedMeetingId, setSelectedMeetingId] = useState(null);
  const [viewMode, setViewMode] = useState("grid"); // 'grid' or 'list'
  const [activeMenu, setActiveMenu] = useState(null);
  const [groups, setGroups] = useState([]);
  const [updatingGroup, setUpdatingGroup] = useState(null);

  // Pagination state
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(12);
  const [totalItems, setTotalItems] = useState(0);
  const [totalPages, setTotalPages] = useState(0);
  const [refreshKey, setRefreshKey] = useState(0);

  useEffect(() => {
    if (isAuthenticated && user) {
      loadMeetings();
      loadGroups();
    }

    const handleRefresh = () => {
      if (isAuthenticated && user) {
        loadMeetings();
        loadGroups();
      }
    };

    window.addEventListener("refresh-meetings", handleRefresh);
    return () => {
      window.removeEventListener("refresh-meetings", handleRefresh);
    };
  }, [isAuthenticated, user, currentPage, pageSize, debouncedSearch, refreshKey]);

  const loadMeetings = async () => {
    if (!isAuthenticated || !user) {
      setLoading(false);
      return;
    }

    try {
      setLoading(true);
      const response = await fetchMeetings({
        query: debouncedSearch,
        page: currentPage,
        pageSize: pageSize,
      });

      setMeetings(response.meetings || []);
      setTotalItems(response.pagination?.total_items || 0);
      setTotalPages(response.pagination?.total_pages || 0);
    } catch (error) {
      console.error("Failed to load meetings:", error);
      setMeetings([]);
      setTotalItems(0);
      setTotalPages(0);
    } finally {
      setLoading(false);
    }
  };

  const loadGroups = async () => {
    try {
      const userId = user.localAccountId || user.id;
      const response = await apiClient.get(`/groups?user_id=${userId}`);
      if (response.data?.status === "success") {
        setGroups(response.data.groups || []);
      }
    } catch (error) {
      console.error("Failed to load groups:", error);
      setGroups([]);
    }
  };

  const handleUpdateMeetingGroup = async (meetingId, groupId) => {
    try {
      setUpdatingGroup(meetingId);
      const response = await apiClient.post("/update_meeting_obj", {
        meeting_id: meetingId,
        updates: { group_id: groupId || null },
      });

      if (response.data?.status === "success") {
        setMeetings((prev) =>
          prev.map((m) =>
            m.id === meetingId ? { ...m, group_id: groupId } : m
          )
        );
      } else {
        throw new Error(response.data?.message || "Failed to update meeting group");
      }
    } catch (error) {
      console.error("Failed to update meeting group:", error);
      alert("Failed to update meeting group");
    } finally {
      setUpdatingGroup(null);
      setActiveMenu(null);
    }
  };

  const handleDelete = async (meetingId, meetingTitle) => {
    if (window.confirm(`Are you sure you want to delete "${meetingTitle}"?`)) {
      try {
        await deleteMeeting(meetingId);
        loadMeetings();
      } catch (error) {
        console.error("Failed to delete meeting:", error);
        alert("Failed to delete meeting");
      }
    }
    setActiveMenu(null);
  };

  const handleViewDetails = useCallback((meeting) => {
    setSelectedMeetingId(meeting.id);
    setShowMeetingDetails(true);
  }, []);

  const handleBackToMeetings = useCallback(() => {
    setShowMeetingDetails(false);
    setSelectedMeetingId(null);
  }, []);

  const handleRefresh = useCallback(() => {
    setCurrentPage(1);
    setSearchTerm("");
    setDebouncedSearch("");
    setRefreshKey((k) => k + 1);
  }, []);

  const handleSearchChange = useCallback((e) => {
    const value = e.target.value;
    setSearchTerm(value);
    if (searchDebounceRef.current) clearTimeout(searchDebounceRef.current);
    searchDebounceRef.current = setTimeout(() => {
      setDebouncedSearch(value);
      setCurrentPage(1);
    }, 500);
  }, []);

  const formatDateTime = useCallback((dateTimeString) => {
    const date = new Date(dateTimeString);
    return {
      date: date.toLocaleDateString("en-US", {
        year: "numeric",
        month: "short",
        day: "numeric",
      }),
      time: date.toLocaleTimeString("en-US", {
        hour: "2-digit",
        minute: "2-digit",
      }),
    };
  }, []);

  const todayMeetings = useMemo(() => {
    const today = new Date().toDateString();
    return meetings.filter((m) => new Date(m.dateTime).toDateString() === today).length;
  }, [meetings]);

  const thisWeekMeetings = useMemo(() => {
    const now = new Date();
    const weekAgo = new Date(now.getTime() - 7 * 24 * 60 * 60 * 1000);
    return meetings.filter((m) => {
      const d = new Date(m.dateTime);
      return d >= weekAgo && d <= now;
    }).length;
  }, [meetings]);

  if (showMeetingDetails) {
    return (
      <MeetingDetails
        meetingId={selectedMeetingId}
        onBack={handleBackToMeetings}
      />
    );
  }

  return (
    <div className="min-h-screen" style={{ background: "#F5F3FF" }}>
      <div className="max-w-7xl mx-auto px-6 py-12">
        {/* Header Section */}
        <div className="flex items-start justify-between mb-12 flex-wrap gap-4">
          <div>
            <h1 className="text-3xl font-bold text-gray-900 mb-1 tracking-tight">
              Meetings
            </h1>
            <p className="text-base text-gray-600">
              Manage and review all your meeting recordings
            </p>
          </div>
          <button
            onClick={handleRefresh}
            className="inline-flex items-center gap-2 px-6 py-3 text-white font-semibold rounded-lg transition-all duration-200 shadow-sm"
            style={{ backgroundColor: "#e04665" }}
            onMouseEnter={(e) =>
              (e.currentTarget.style.backgroundColor = "#e63950")
            }
            onMouseLeave={(e) =>
              (e.currentTarget.style.backgroundColor = "#e04665")
            }
          >
            <RefreshCw size={18} />
            <span>Refresh</span>
          </button>
        </div>

        {/* Statistics Cards */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-12">
          {/* Total Meetings */}
          <div className="bg-white border border-gray-200 rounded-xl p-6 hover:shadow-lg transition-all duration-300">
            <div className="flex items-center justify-between mb-4">
              <div
                className="w-12 h-12 rounded-lg flex items-center justify-center text-white"
                style={{ backgroundColor: "#e04665" }}
              >
                <Video size={24} />
              </div>
            </div>
            <div className="text-4xl font-bold text-gray-800 mb-1">
              {totalItems}
            </div>
            <div className="text-sm text-gray-600 font-medium">
              Total Meetings
            </div>
          </div>

          {/* Today's Meetings */}
          <div className="bg-white border border-gray-200 rounded-xl p-6 hover:shadow-lg transition-all duration-300">
            <div className="flex items-center justify-between mb-4">
              <div className="w-12 h-12 rounded-lg bg-emerald-500 flex items-center justify-center text-white">
                <Calendar size={24} />
              </div>
            </div>
            <div className="text-4xl font-bold text-gray-800 mb-1">
              {todayMeetings}
            </div>
            <div className="text-sm text-gray-600 font-medium">Today</div>
          </div>

          {/* This Week */}
          <div className="bg-white border border-gray-200 rounded-xl p-6 hover:shadow-lg transition-all duration-300">
            <div className="flex items-center justify-between mb-4">
              <div className="w-12 h-12 rounded-lg bg-amber-500 flex items-center justify-center text-white">
                <TrendingUp size={24} />
              </div>
            </div>
            <div className="text-4xl font-bold text-gray-800 mb-1">
              {thisWeekMeetings}
            </div>
            <div className="text-sm text-gray-600 font-medium">This Week</div>
          </div>

          {/* Current Page */}
          <div className="bg-white border border-gray-200 rounded-xl p-6 hover:shadow-lg transition-all duration-300">
            <div className="flex items-center justify-between mb-4">
              <div
                className="w-12 h-12 rounded-lg flex items-center justify-center text-white"
                style={{ backgroundColor: "#e04665" }}
              >
                <FileText size={24} />
              </div>
            </div>
            <div className="text-4xl font-bold text-gray-800 mb-1">
              {meetings.length}
            </div>
            <div className="text-sm text-gray-600 font-medium">
              Current Page
            </div>
          </div>
        </div>

        {/* Search and Filters */}
        <div className="flex gap-4 mb-8 items-center flex-wrap">
          <div className="flex-1 min-w-[300px] relative">
            <Search
              className="absolute left-4 top-1/2 -translate-y-1/2 text-gray-400 pointer-events-none"
              size={20}
            />
            <input
              type="text"
              className="w-full pl-12 pr-4 py-3 bg-white border border-gray-300 rounded-lg text-gray-800 placeholder-gray-400 focus:outline-none transition-all"
              style={{
                borderColor: searchTerm ? "#e04665" : "",
                boxShadow: searchTerm
                  ? "0 0 0 3px rgba(224, 71, 101, 0.1)"
                  : "",
              }}
              placeholder="Search meetings..."
              value={searchTerm}
              onChange={handleSearchChange}
            />
          </div>

          <div className="flex gap-2">
            <button
              className={`w-10 h-10 flex items-center justify-center rounded-lg border transition-all ${
                viewMode === "grid"
                  ? "text-white shadow-sm"
                  : "border-gray-300 text-gray-600 hover:text-gray-800 bg-white"
              }`}
              style={
                viewMode === "grid"
                  ? { backgroundColor: "#e04665", borderColor: "#e04665" }
                  : {}
              }
              onClick={() => setViewMode("grid")}
              title="Grid View"
            >
              <Grid size={20} />
            </button>
            <button
              className={`w-10 h-10 flex items-center justify-center rounded-lg border transition-all ${
                viewMode === "list"
                  ? "text-white shadow-sm"
                  : "border-gray-300 text-gray-600 hover:text-gray-800 bg-white"
              }`}
              style={
                viewMode === "list"
                  ? { backgroundColor: "#e04665", borderColor: "#e04665" }
                  : {}
              }
              onClick={() => setViewMode("list")}
              title="List View"
            >
              <List size={20} />
            </button>
          </div>
        </div>

        {/* Meetings Grid/List */}
        {loading ? (
          <div className="flex flex-col items-center justify-center py-20">
            <div
              className="w-12 h-12 border-4 border-purple-200 rounded-full animate-spin"
              style={{ borderTopColor: "#e04665" }}
            ></div>
            <p className="mt-4 text-lg font-medium text-gray-700">
              Loading meetings...
            </p>
          </div>
        ) : meetings.length === 0 ? (
          <div className="flex flex-col items-center justify-center py-20 text-center">
            <div className="text-purple-300 mb-6 opacity-50">
              <Video size={80} strokeWidth={1.5} />
            </div>
            <h3 className="text-xl font-semibold text-gray-800 mb-2">
              {searchTerm ? "No meetings found" : "No meetings yet"}
            </h3>
            <p className="text-gray-600 max-w-md">
              {searchTerm
                ? "Try adjusting your search."
                : "Create your first meeting to get started."}
            </p>
          </div>
        ) : (
          <div
            className={
              viewMode === "grid"
                ? "grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6"
                : "flex flex-col gap-4"
            }
          >
            {meetings.map((meeting) => {
              const { date, time } = formatDateTime(meeting.dateTime);
              return (
                <div
                  key={meeting.id}
                  className="group bg-white border border-gray-200 rounded-xl overflow-hidden hover:shadow-xl transition-all duration-300 cursor-pointer hover:-translate-y-1"
                  onClick={() => handleViewDetails(meeting)}
                >
                  {/* Card Header */}
                  <div className="p-4 bg-purple-50/30 border-b border-gray-200">
                    <div className="flex items-start justify-between gap-2 mb-2">
                      <h3
                        className="font-semibold text-gray-800 text-sm transition-colors line-clamp-2 flex-1"
                        style={{ color: "inherit" }}
                        onMouseEnter={(e) =>
                          (e.currentTarget.style.color = "#e04665")
                        }
                        onMouseLeave={(e) =>
                          (e.currentTarget.style.color = "inherit")
                        }
                      >
                        {meeting.title}
                      </h3>
                      <div
                        className="relative opacity-0 group-hover:opacity-100 transition-opacity"
                        onClick={(e) => e.stopPropagation()}
                      >
                        <button
                          className="p-1 rounded"
                          style={{ backgroundColor: "transparent" }}
                          onMouseEnter={(e) =>
                            (e.currentTarget.style.backgroundColor =
                              "rgba(224, 71, 101, 0.1)")
                          }
                          onMouseLeave={(e) =>
                            (e.currentTarget.style.backgroundColor =
                              "transparent")
                          }
                          onClick={() =>
                            setActiveMenu(
                              activeMenu === meeting.id ? null : meeting.id
                            )
                          }
                        >
                          <MoreVertical size={16} className="text-gray-600" />
                        </button>
                        {activeMenu === meeting.id && (
                          <div className="absolute top-full right-0 mt-1 bg-white rounded-lg shadow-xl min-w-[200px] max-w-[250px] p-1 z-10 border border-gray-200 max-h-[400px] overflow-y-auto custom-scrollbar">
                            <button
                              className="w-full px-3 py-2 text-left text-sm font-medium text-gray-700 rounded-md flex items-center gap-2 transition-colors"
                              onMouseEnter={(e) =>
                                (e.currentTarget.style.backgroundColor =
                                  "rgba(224, 71, 101, 0.05)")
                              }
                              onMouseLeave={(e) =>
                                (e.currentTarget.style.backgroundColor =
                                  "transparent")
                              }
                              onClick={() => handleViewDetails(meeting)}
                            >
                              <Eye size={16} />
                              View Details
                            </button>

                            {/* Group Selector */}
                            <div className="border-t border-gray-200 my-1 pt-1">
                              <div className="px-3 py-1.5 text-xs font-semibold text-gray-500 uppercase tracking-wider">
                                Assign to Group
                              </div>

                              <div className="max-h-[250px] overflow-y-auto custom-scrollbar">
                                {/* No Group Option */}
                                <button
                                  className={`w-full px-3 py-2 text-left text-sm font-medium rounded-md flex items-center gap-2 transition-colors ${
                                    !meeting.group_id
                                      ? "text-white"
                                      : "text-gray-600"
                                  } ${
                                    updatingGroup === meeting.id
                                      ? "opacity-50 cursor-wait"
                                      : ""
                                  }`}
                                  style={
                                    !meeting.group_id
                                      ? {
                                          backgroundColor:
                                            "rgba(224, 71, 101, 0.15)",
                                          color: "#e04665",
                                        }
                                      : {}
                                  }
                                  onMouseEnter={(e) => {
                                    if (meeting.group_id)
                                      e.currentTarget.style.backgroundColor =
                                        "rgba(224, 71, 101, 0.05)";
                                  }}
                                  onMouseLeave={(e) => {
                                    if (meeting.group_id)
                                      e.currentTarget.style.backgroundColor =
                                        "transparent";
                                  }}
                                  onClick={() => {
                                    handleUpdateMeetingGroup(
                                      meeting.id,
                                      null
                                    );
                                  }}
                                  disabled={updatingGroup === meeting.id}
                                >
                                  <FolderOpen
                                    size={16}
                                    className="flex-shrink-0"
                                  />
                                  <span className="truncate flex-1">
                                    No Group
                                  </span>
                                  {!meeting.group_id && (
                                    <Check
                                      size={16}
                                      className="flex-shrink-0"
                                      style={{ color: "#e04665" }}
                                    />
                                  )}
                                </button>

                                {/* Group Options */}
                                {groups.length === 0 ? (
                                  <div className="px-3 py-2 text-xs text-gray-500 italic">
                                    No groups created yet
                                  </div>
                                ) : (
                                  groups.map((group) => {
                                    const isSelected =
                                      meeting.group_id === group.id;
                                    return (
                                      <button
                                        key={group.id}
                                        className={`w-full px-3 py-2 text-left text-sm font-medium rounded-md flex items-center gap-2 transition-colors ${
                                          updatingGroup === meeting.id
                                            ? "opacity-50 cursor-wait"
                                            : ""
                                        }`}
                                        style={
                                          isSelected
                                            ? {
                                                backgroundColor:
                                                  "rgba(224, 71, 101, 0.15)",
                                              }
                                            : {}
                                        }
                                        onMouseEnter={(e) => {
                                          if (!isSelected)
                                            e.currentTarget.style.backgroundColor =
                                              "rgba(224, 71, 101, 0.05)";
                                        }}
                                        onMouseLeave={(e) => {
                                          if (!isSelected)
                                            e.currentTarget.style.backgroundColor =
                                              "transparent";
                                        }}
                                        onClick={() =>
                                          handleUpdateMeetingGroup(
                                            meeting.id,
                                            group.id
                                          )
                                        }
                                        disabled={updatingGroup === meeting.id}
                                      >
                                        <Folder
                                          size={16}
                                          className="flex-shrink-0"
                                          style={{
                                            color: group.color || "#e04665",
                                          }}
                                        />
                                        <span
                                          className="truncate flex-1"
                                          style={{
                                            color: isSelected
                                              ? group.color
                                              : "#4b5563",
                                          }}
                                        >
                                          {group.name}
                                        </span>
                                        {isSelected && (
                                          <Check
                                            size={16}
                                            className="flex-shrink-0"
                                            style={{
                                              color: group.color || "#e04665",
                                            }}
                                          />
                                        )}
                                      </button>
                                    );
                                  })
                                )}
                              </div>
                            </div>

                          </div>
                        )}
                      </div>
                    </div>
                  </div>

                  {/* Card Body */}
                  <div className="p-4 space-y-3">
                    <div className="flex items-center gap-3 text-sm text-gray-600">
                      <Calendar size={16} className="flex-shrink-0" />
                      <span>{date}</span>
                    </div>
                    <div className="flex items-center gap-3 text-sm text-gray-600">
                      <Clock size={16} className="flex-shrink-0" />
                      <span>{time}</span>
                    </div>
                    {meeting.group_id &&
                      groups.find((g) => g.id === meeting.group_id) && (
                        <div className="flex items-center gap-2 text-sm">
                          <Folder
                            size={16}
                            className="flex-shrink-0"
                            style={{
                              color:
                                groups.find((g) => g.id === meeting.group_id)
                                  ?.color || "#8b5cf6",
                            }}
                          />
                          <span
                            className="font-medium truncate"
                            style={{
                              color:
                                groups.find((g) => g.id === meeting.group_id)
                                  ?.color || "#8b5cf6",
                            }}
                          >
                            {
                              groups.find((g) => g.id === meeting.group_id)
                                ?.name
                            }
                          </span>
                        </div>
                      )}
                  </div>

                  {/* Card Footer */}
                  <div className="px-4 py-3 bg-purple-50/30 border-t border-gray-200">
                    <button
                      className="w-full py-2 px-3 text-white text-sm font-medium rounded transition-colors flex items-center justify-center gap-2 shadow-sm"
                      style={{ backgroundColor: "#e04665" }}
                      onMouseEnter={(e) =>
                        (e.currentTarget.style.backgroundColor = "#e63950")
                      }
                      onMouseLeave={(e) =>
                        (e.currentTarget.style.backgroundColor = "#e04665")
                      }
                    >
                      <Eye size={16} />
                      View Details
                    </button>
                  </div>
                </div>
              );
            })}
          </div>
        )}

        {/* Pagination */}
        {totalPages > 1 && (
          <div className="flex items-center justify-center gap-2 mt-8">
            <button
              className="p-2 rounded-lg border border-gray-300 bg-white text-gray-600 disabled:opacity-50 disabled:cursor-not-allowed hover:border-orange-400 hover:text-gray-800 transition-all"
              onClick={() => setCurrentPage(Math.max(1, currentPage - 1))}
              disabled={currentPage === 1}
            >
              <ChevronLeft size={20} />
            </button>
            <span className="text-sm text-gray-700 font-medium">
              Page {currentPage} of {totalPages}
            </span>
            <button
              className="p-2 rounded-lg border border-gray-300 bg-white text-gray-600 disabled:opacity-50 disabled:cursor-not-allowed hover:text-gray-800 transition-all"
              onMouseEnter={(e) => {
                if (!e.currentTarget.disabled)
                  e.currentTarget.style.borderColor = "#e04665";
              }}
              onMouseLeave={(e) => (e.currentTarget.style.borderColor = "")}
              onClick={() =>
                setCurrentPage(Math.min(totalPages, currentPage + 1))
              }
              disabled={currentPage === totalPages}
            >
              <ChevronRight size={20} />
            </button>
          </div>
        )}
      </div>
    </div>
  );
};

export default Meetings;
